import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PanchangPanel({ profile }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    if (!profile) return;
    const useDate = d ?? date;
    setLoading(true);
    try {
      const r = await api.panchang({
        date: useDate, lat: profile.lat, lon: profile.lon,
        tz: profile.tz, ayanamsha: profile.ayanamsha,
      });
      setData(r);
    } catch (err) {
      console.error("panchang failed:", err);
      toast.error("Panchang failed");
    }
    setLoading(false);
  }, [profile, date]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.profile_id]);

  if (!profile) return <div className="text-[#A19E95] font-mono text-sm">Select a profile first.</div>;

  return (
    <div className="space-y-6" data-testid="panchang-panel">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Date</div>
          <Input data-testid="panchang-date" type="date" value={date}
                 onChange={e => setDate(e.target.value)}
                 className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-48" />
        </div>
        <Button onClick={() => load()} disabled={loading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">
          {loading ? "Loading…" : "Load Panchang"}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ["Vara", `${data.vara} · ${data.vara_lord}`],
              ["Tithi", `${data.tithi.name} (${data.tithi.percent_complete}%)`],
              ["Nakshatra", `${data.nakshatra.name} (${data.nakshatra.percent_complete}%)`],
              ["Yoga", data.yoga.name],
              ["Karana", data.karana],
              ["Sunrise / Sunset", `${data.sunrise} / ${data.sunset}`],
            ].map(([k, v]) => (
              <div key={k} className="border border-[#2A2D35] bg-[#181A1F] p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">{k}</div>
                <div className="mt-1 text-[#E6E1D6] font-heading text-lg leading-tight">{v}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              ["Rahu Kala", data.rahu_kala, "#C15C3D"],
              ["Yamaganda Kala", data.yamaganda_kala, "#E5C158"],
              ["Gulika Kala", data.gulika_kala, "#A8896C"],
            ].map(([k, v, color]) => (
              <div key={k} className="border p-3 bg-[#181A1F]" style={{ borderColor: color }}>
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color }}>{k}</div>
                <div className="mt-1 font-mono text-[#E6E1D6]">
                  {v ? `${v.start} – ${v.end}` : "—"}
                </div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="font-heading text-xl text-[#D4AF37] mb-3">Day Muhurtas</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {data.day_muhurtas.map((m) => (
                <div key={`day-${m.name}`} className="border border-[#2A2D35] p-2 bg-[#181A1F]"
                     style={{ borderLeftColor: m.quality === "auspicious" ? "#7B906F" : "#C15C3D",
                              borderLeftWidth: 3 }}>
                  <div className="font-heading text-sm text-[#E6E1D6]">{m.name}</div>
                  <div className="font-mono text-[10px] text-[#A19E95]">{m.start} – {m.end}</div>
                  <div className="font-mono text-[10px] mt-0.5"
                       style={{ color: m.quality === "auspicious" ? "#7B906F" : "#C15C3D" }}>
                    {m.quality}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-heading text-xl text-[#D4AF37] mb-3">Night Muhurtas</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {data.night_muhurtas.map((m) => (
                <div key={`night-${m.name}`} className="border border-[#2A2D35] p-2 bg-[#181A1F]"
                     style={{ borderLeftColor: m.quality === "auspicious" ? "#7B906F" : "#C15C3D",
                              borderLeftWidth: 3 }}>
                  <div className="font-heading text-sm text-[#E6E1D6]">{m.name}</div>
                  <div className="font-mono text-[10px] text-[#A19E95]">{m.start} – {m.end}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
